import { useEffect, useRef, useState } from "react";

/**
 * SignaturePad
 * ------------
 * A small draw-to-sign canvas with optional "typed name" gate.
 *
 * Props:
 *   onSave(dataUrl, name?)  called with the PNG data-URL. If `nameField`
 *                            is true, the second arg is the typed name.
 *   label                    caption above the canvas.
 *   testIdPrefix             prefix for data-testids.
 *   nameField                when true, render a required text input above
 *                            the canvas — used on client sign-off so we
 *                            capture WHO signed if someone signs on the
 *                            customer's behalf. [Iter 331 — 2026-02-24]
 *   nameLabel                caption for the name input (defaults to
 *                            "Print name (person signing)").
 *   defaultName              seed the name input (e.g. the logged-in
 *                            user's name for tech sign-off).
 */
export default function SignaturePad({
  onSave,
  label = "Sign here",
  testIdPrefix = "sig",
  nameField = false,
  nameLabel = "Print name (person signing)",
  defaultName = "",
}) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);
  const [name, setName] = useState(defaultName);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0a0b0e";
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };

  const start = (e) => {
    drawing.current = true;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setEmpty(false);
  };
  const end = () => (drawing.current = false);

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setEmpty(true);
  };

  const save = () => {
    const data = canvasRef.current.toDataURL("image/png");
    if (nameField) {
      onSave(data, name.trim());
    } else {
      onSave(data);
    }
  };

  const nameOk = !nameField || name.trim().length >= 2;
  const disabled = empty || !nameOk;

  return (
    <div className="space-y-2">
      {nameField && (
        <div>
          <label
            className="text-xs uppercase tracking-widest text-[#647087] block mb-1"
            htmlFor={`${testIdPrefix}-name`}
          >
            {nameLabel}
          </label>
          <input
            id={`${testIdPrefix}-name`}
            data-testid={`${testIdPrefix}-name`}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name of signee"
            autoComplete="name"
            className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-[#647087] focus:outline-none focus:border-white/30"
          />
        </div>
      )}
      <div className="text-xs uppercase tracking-widest text-[#647087]">{label}</div>
      <canvas
        ref={canvasRef}
        data-testid={`${testIdPrefix}-canvas`}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        className="w-full h-40 bg-white border-2 border-dashed border-white/30 rounded-md touch-none cursor-crosshair"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={clear}
          data-testid={`${testIdPrefix}-clear`}
          className="hv-btn hv-btn-ghost flex-1"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={save}
          disabled={disabled}
          data-testid={`${testIdPrefix}-save`}
          className="hv-btn hv-btn-primary flex-1 disabled:opacity-50"
        >
          Save signature
        </button>
      </div>
      {nameField && !nameOk && !empty && (
        <div className="text-xs text-amber-400" data-testid={`${testIdPrefix}-name-hint`}>
          Please type the signee's name before saving.
        </div>
      )}
    </div>
  );
}
